import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApp } from '../context/AppContext.jsx';
import { generateNextDay } from '../services/anthropic.js';
import FoxMark from '../components/shared/FoxMark.jsx';
import { authenticatedFetch } from '../services/apiClient.js';

const PAPER = '#F2EBDC';
const INK = '#1B1814';

export default function SubscribeSuccess() {
  const navigate = useNavigate();
  const { user } = useUser();
  const {
    setSubscription,
    userProfile,
    dailyFeedback,
    intakeMessages,
    addWeek,
    guide,
    weeks,
  } = useApp();

  const hasStartedRef = useRef(false);
  const [isGenerating, setIsGenerating] = useState(true);
  const [week2Ready, setWeek2Ready] = useState(false);
  const [generationError, setGenerationError] = useState('');

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    setSubscription({
      status: 'active',
      startedAt: new Date().toISOString(),
    });
    setIsGenerating(true);
    generateWeek2().finally(() => setIsGenerating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generateWeek2() {
    console.log('[SubscribeSuccess] Starting Week 2 generation');
    console.log('[SubscribeSuccess] userProfile:', userProfile);
    console.log(
      '[SubscribeSuccess] dailyFeedback:',
      dailyFeedback?.length,
      'entries'
    );

    try {
      // Build no-repeat content set from Week 1 so Week 2 doesn't
      // echo a scripture or theme the user already saw.
      const week1Days =
        (weeks || []).find((w) => w.weekNumber === 1)?.days || [];
      const usedContent = {
        scriptures: week1Days
          .map((d) => d.scripture_reference)
          .filter(Boolean),
        themes: week1Days.map((d) => d.title).filter(Boolean),
        pullQuotes: week1Days.map((d) => d.pull_quote).filter(Boolean),
      };

      const day1 = await generateNextDay(
        userProfile,
        1,
        2,
        [],
        dailyFeedback || [],
        intakeMessages || [],
        usedContent
      );

      console.log(
        '[SubscribeSuccess] Week 2 Day 1 generated:',
        day1?.title
      );

      const week2 = {
        weekNumber: 2,
        week_title: `Week 2 — ${day1.title || 'Going deeper'}`,
        week_theme: day1.subtitle || '',
        opening_statement: `Welcome back, ${userProfile?.name || 'friend'}. Week 2 goes deeper.`,
        days: [day1],
        totalDays: 5,
      };

      addWeek(2, week2);
      console.log('[SubscribeSuccess] Week 2 added to context');

      // Persist to Supabase too — addWeek already fires this for signed-in
      // users via AppContext, but call directly here as well so the
      // moment-of-payment write is guaranteed even if the context save
      // path is mid-hydration or otherwise suppressed.
      if (user?.id) {
        try {
          await authenticatedFetch('/api/save-data', {
            method: 'POST',
            body: JSON.stringify({
              type: 'week',
              data: week2,
            }),
          });
          console.log('[SubscribeSuccess] Week 2 saved to Supabase');
        } catch (e) {
          console.warn(
            '[SubscribeSuccess] Supabase save failed (non-blocking):',
            e?.message
          );
        }
      }

      setWeek2Ready(true);
    } catch (err) {
      console.error('[SubscribeSuccess] Error generating Week 2:', err);
      setGenerationError(err?.message || 'Generation failed');
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAPER,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          marginBottom: 24,
          animation: isGenerating
            ? 'fox-pulse 2s ease-in-out infinite'
            : 'none',
        }}
      >
        <FoxMark variant="gold" size={64} />
      </div>

      {isGenerating && !generationError && (
        <>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 26,
              fontWeight: 300,
              color: INK,
              marginBottom: 12,
            }}
          >
            Welcome back
          </h1>
          <p
            style={{
              fontSize: 15,
              color: INK,
              opacity: 0.6,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              lineHeight: 1.7,
              maxWidth: 320,
            }}
          >
            {guide?.name || 'Your guide'} is preparing your Week 2 now —
            drawing from everything you shared and every check-in along
            the way.
          </p>
          <p
            style={{
              fontSize: 12,
              color: INK,
              opacity: 0.3,
              marginTop: 16,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
            }}
          >
            This usually takes about a minute…
          </p>
        </>
      )}

      {week2Ready && !generationError && (
        <>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 26,
              fontWeight: 300,
              color: INK,
              marginBottom: 12,
            }}
          >
            Week 2 is ready
          </h1>
          <p
            style={{
              fontSize: 15,
              color: INK,
              opacity: 0.6,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              lineHeight: 1.7,
              maxWidth: 320,
              marginBottom: 32,
            }}
          >
            {guide?.name || 'Your guide'} has prepared something deeper
            for you this week.
          </p>
          <button
            type="button"
            onClick={() => navigate('/study/week/2')}
            style={{
              background: INK,
              color: PAPER,
              border: 'none',
              borderRadius: 12,
              padding: '16px 40px',
              fontSize: 16,
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: 'Georgia, serif',
            }}
          >
            Begin Week 2 →
          </button>
        </>
      )}

      {generationError && (
        <>
          <p
            style={{
              fontSize: 15,
              color: '#c93535',
              marginBottom: 20,
            }}
          >
            Something went wrong generating Week 2.
          </p>
          <button
            type="button"
            onClick={() => {
              setGenerationError('');
              setIsGenerating(true);
              generateWeek2().finally(() => setIsGenerating(false));
            }}
            style={{
              background: INK,
              color: PAPER,
              border: 'none',
              borderRadius: 12,
              padding: '14px 28px',
              fontSize: 15,
              cursor: 'pointer',
              marginBottom: 12,
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => navigate('/study')}
            style={{
              background: 'none',
              border: 'none',
              color: INK,
              opacity: 0.4,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Go to my study
          </button>
        </>
      )}

      <style>{`
        @keyframes fox-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.08); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
